import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Typography,
} from "@mui/material";

import useAdminOrderFormData from "../../../hooks/useAdminOrderFormData";

import StepCustomerInfo from "./StepCustomerInfo";
import StepFulfillmentInfo from "./StepFulfillmentInfo";
import StepOrderItems from "./StepOrderItems";
import StepReview from "./StepReview";
import StepPayment from "./StepPayment";
import SubmitActions from "./SubmitActions";

const steps = [
  "Customer Info",
  "Fulfillment",
  "Order Items",
  "Payment",
  "Review",
];

const AdminOrderForm = () => {
  const {
    step,
    nextStep,
    prevStep,
    goToStep,
    formData,
    setFormData,
    formErrors,
    addItem,
    updateItem,
    removeItem,
    updateSummary,
    validateStep,
    validateAll,
    resetFormData,
  } = useAdminOrderFormData();

  const location = useLocation();

  useEffect(() => {
    if (location.state?.draft?.formData) {
      const fullDraft = location.state.draft;
      setFormData({
        ...fullDraft.formData,
        meta: {
          ...(fullDraft.formData.meta || {}),
          draftId: fullDraft._id, // ✅ Add draftId to meta
        },
      });
      goToStep(0);
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.draft]);

  const updateInternalField = (updates) => {
    setFormData((prev) => ({
      ...prev,
      internal: {
        ...prev.internal,
        ...updates,
      },
    }));
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <StepCustomerInfo
            formData={formData}
            setFormData={setFormData}
            errors={formErrors}
          />
        );
      case 1:
        return (
          <StepFulfillmentInfo
            formData={formData}
            setFormData={setFormData}
            errors={formErrors}
          />
        );
      case 2:
        return (
          <StepOrderItems
            formData={formData}
            addItem={addItem}
            updateItem={updateItem}
            removeItem={removeItem}
            updateInternal={updateInternalField}
            errors={formErrors}
          />
        );
      case 3:
        return (
          <StepPayment
            formData={formData}
            setFormData={setFormData}
            updateSummary={updateSummary}
            errors={formErrors}
          />
        );
      case 4:
        return <StepReview formData={formData} />;

      default:
        return null;
    }
  };

  return (
    <Box maxWidth="md" mx="auto" px={2} py={4}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Admin Order Entry
      </Typography>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label} onClick={() => goToStep(index)}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        {renderStepContent()}
      </Paper>

      <SubmitActions
        step={step}
        isLastStep={step === steps.length - 1}
        nextStep={nextStep}
        prevStep={prevStep}
        goToStep={goToStep}
        validateStep={validateStep}
        validateAll={validateAll}
        formData={formData}
        resetFormData={resetFormData}
      />
    </Box>
  );
};

export default AdminOrderForm;
